import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Badge, Button } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import {
    FaPassport,
    FaDiscord,
    FaPlaneArrival,
    FaMapMarkedAlt,
    FaTerminal,
    FaExclamationTriangle,
    FaLock,
    FaGavel,
    FaUserCheck,
    FaUserSlash,
    FaExternalLinkAlt
} from 'react-icons/fa';
import RedirectModal from '../components/RedirectModal';
import SEO from '../components/SEO';

const Info = () => {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('whitelisting');
    const [showModal, setShowModal] = useState(false);
    const [redirectUrl, setRedirectUrl] = useState('');

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tabParam = location.state?.tab || queryParams.get('tab');
        if (tabParam === 'isolation' || tabParam === 'whitelisting') {
            setActiveTab(tabParam);
        }
    }, [location]);

    const handleDiscordClick = () => {
        setRedirectUrl('https://discord.com/channels/736468217580486766/1529433606835933274/1539966069614317599');
        setShowModal(true);
    };

    const handleConfirmRedirect = () => {
        window.open(redirectUrl, '_blank', 'noopener,noreferrer');
        setShowModal(false);
    };

    const visaSteps = [
        {
            number: "01",
            title: "Discord Access Role",
            icon: FaDiscord,
            color: "#5865F2",
            badge: "COMMUNITY STEP",
            heading: "Claim Your Server Connect Role",
            description: "Prior to joining the city, you must obtain your server access role via our Discord Reaction Role system. This unlocks direct connection permission.",
            action: (
                <Button
                    className="btn-sm d-inline-flex align-items-center gap-2 mt-3 fw-bold border-0"
                    style={{
                        backgroundColor: '#5865F2',
                        color: '#ffffff',
                        borderRadius: '8px',
                        padding: '0.5rem 1rem',
                        boxShadow: '0 0 15px rgba(88, 101, 242, 0.4)',
                        transition: 'all 0.3s ease'
                    }}
                    onClick={handleDiscordClick}
                >
                    <FaExternalLinkAlt size={14} /> Claim Reaction Role Channel
                </Button>
            )
        },
        {
            number: "02",
            title: "Tourist Arrival Status",
            icon: FaPassport,
            color: "#00f3ff",
            badge: "ENTRY PHASE",
            heading: "Initial City Restrictions",
            description: "Upon your first connection, you will hold 'Tourist' status. As a Tourist, city employment, criminal ops, and official activities remain locked until your Permanent Visa is granted."
        },
        {
            number: "03",
            title: "Airport Guidance & Transport",
            icon: FaPlaneArrival,
            color: "#ffd700",
            badge: "LSIA SPAWN",
            heading: "Interact with Airport Guidance PED",
            description: "When your character arrives at Los Santos International Airport, locate the official Guidance PED. Press 'E' to open the dialogue menu for initial city instructions. Rental vehicles are available nearby."
        },
        {
            number: "04",
            title: "NPC Evaluation Network",
            icon: FaMapMarkedAlt,
            color: "#bc13fe",
            badge: "IN-GAME TASKS",
            heading: "Meet City Officials & Track Progress",
            description: "Traverse the city to interact with key NPC PEDs across different locations. You can review your real-time visa progress using the in-game slash command:",
            command: "/visa"
        },
        {
            number: "05",
            title: "Immigration Approval",
            icon: FaUserCheck,
            color: "#0aff00",
            badge: "FINAL CLEARANCE",
            heading: "Government Office Validation",
            description: "After completing all required PED interactions, visit the Immigration Officer inside the Government Building. Our automated backend evaluates your interaction score. Passing candidates receive their Permanent Visa to start full RP!"
        }
    ];

    return (
        <div style={{ minHeight: '100vh', paddingTop: '60px' }}>
            <SEO
                title="Server Info & Whitelist Guide"
                description="Everything you need to know about joining Thalai Nagaram RP (TNRP): Discord character whitelisting process, FiveM server connection, in-game visa evaluation, and server protocols."
                keywords="TNRP Whitelist, How to join Thalai Nagaram RP, Tamil FiveM Whitelist, GTA 5 Tamil Roleplay Guide, TNRP Visa Evaluation, FiveM Connect Tamil"
                canonicalUrl="https://thalainagaram.org/info"
            />
            <RedirectModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={handleConfirmRedirect}
                targetUrl={redirectUrl}
                variant="external"
            />

            {/* Ambient Background Accents */}
            <div style={{ position: 'fixed', top: '15%', right: '-10%', width: '45vw', height: '45vw', background: 'radial-gradient(circle, rgba(0, 243, 255, 0.08) 0%, transparent 60%)', filter: 'blur(90px)', zIndex: -1 }} />
            <div style={{ position: 'fixed', bottom: '10%', left: '-10%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, rgba(255, 0, 60, 0.08) 0%, transparent 60%)', filter: 'blur(90px)', zIndex: -1 }} />

            <Container fluid className="py-5 px-lg-5">
                {/* Main Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-5 pb-2"
                >
                    <div
                        className="d-inline-block px-3 py-1 mb-3 border border-warning border-opacity-50 text-neon-gold tracking-widest small fw-bold"
                        style={{ fontFamily: 'Orbitron, sans-serif' }}
                    >
                        TNRP CITY PROTOCOLS
                    </div>
                    <h1 className="display-4 fw-bold text-white mb-3" style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '2.4rem' }}>
                        CITY <span className="text-neon-cyan">INFORMATION</span> & <span className="text-neon-gold">SYSTEMS</span>
                    </h1>
                    <p className="lead mx-auto mb-0 fw-semibold" style={{ maxWidth: '750px', color: '#f1f5f9', fontSize: '1.1rem' }}>
                        Everything you need to know regarding character whitelisting, in-game visa evaluation, and server discipline protocols.
                    </p>
                </motion.div>

                {/* Subtab Navigation Buttons */}
                <div className="d-flex justify-content-center mb-5">
                    <div
                        className="p-1.5 rounded-pill d-inline-flex gap-2"
                        style={{
                            background: 'rgba(15, 15, 15, 0.9)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(10px)'
                        }}
                    >
                        <Button
                            variant="none"
                            className={`px-4 py-2.5 rounded-pill fw-bold d-flex align-items-center gap-2 transition-all ${activeTab === 'whitelisting' ? 'btn-neon-active' : ''
                                }`}
                            onClick={() => setActiveTab('whitelisting')}
                            style={{
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: '0.95rem',
                                background: activeTab === 'whitelisting' ? 'linear-gradient(90deg, var(--neon-cyan), #00a8ff)' : 'transparent',
                                color: activeTab === 'whitelisting' ? '#000' : '#e2e8f0',
                                boxShadow: activeTab === 'whitelisting' ? '0 0 20px rgba(0, 243, 255, 0.4)' : 'none',
                                border: 'none'
                            }}
                        >
                            <FaPassport size={18} /> WHITELISTING & VISA PROCESS
                        </Button>

                        <Button
                            variant="none"
                            className={`px-4 py-2.5 rounded-pill fw-bold d-flex align-items-center gap-2 transition-all ${activeTab === 'isolation' ? 'btn-neon-active' : ''
                                }`}
                            onClick={() => setActiveTab('isolation')}
                            style={{
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: '0.95rem',
                                background: activeTab === 'isolation' ? 'linear-gradient(90deg, #ff003c, #ff416c)' : 'transparent',
                                color: activeTab === 'isolation' ? '#fff' : '#e2e8f0',
                                boxShadow: activeTab === 'isolation' ? '0 0 20px rgba(255, 0, 60, 0.4)' : 'none',
                                border: 'none'
                            }}
                        >
                            <FaLock size={18} /> BAN & ISOLATION SYSTEM
                        </Button>
                    </div>
                </div>

                {/* Subtab Content Area */}
                <AnimatePresence mode="wait">
                    {activeTab === 'whitelisting' ? (
                        <motion.div
                            key="whitelisting-tab"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Section Header */}
                            <div className="d-flex align-items-center mb-4">
                                <div className="p-3 me-3 rounded-3" style={{ background: 'rgba(0, 243, 255, 0.15)', border: '1px solid var(--neon-cyan)', color: 'var(--neon-cyan)' }}>
                                    <FaPassport size={28} />
                                </div>
                                <div>
                                    <h2 className="h4 fw-bold text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                        WHITELISTING & IN-GAME VISA JOURNEY
                                    </h2>
                                    <p className="mb-0 fw-semibold" style={{ color: '#cbd5e1', fontSize: '1rem' }}>
                                        Follow this official 5-step roadmap to earn your citizenship visa
                                    </p>
                                </div>
                            </div>

                            <Row className="g-4">
                                {visaSteps.map((step, idx) => (
                                    <Col lg={4} md={6} key={idx}>
                                        <div
                                            className="h-100 p-4 position-relative overflow-hidden d-flex flex-column"
                                            style={{
                                                background: 'rgba(22, 26, 34, 0.85)',
                                                backdropFilter: 'blur(12px)',
                                                border: `1px solid rgba(255, 255, 255, 0.12)`,
                                                borderTop: `3px solid ${step.color}`,
                                                borderRadius: '16px',
                                                boxShadow: '0 8px 30px rgba(0, 0, 0, 0.5)'
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center mb-3">
                                                <span
                                                    className="fw-bold display-6"
                                                    style={{ fontFamily: 'Orbitron, sans-serif', color: step.color }}
                                                >
                                                    {step.number}
                                                </span>
                                                <Badge
                                                    bg="transparent"
                                                    className="px-2.5 py-1 border fw-bold"
                                                    style={{ borderColor: `${step.color}`, color: '#ffffff', backgroundColor: `${step.color}30`, letterSpacing: '1px', fontSize: '0.7rem' }}
                                                >
                                                    {step.badge}
                                                </Badge>
                                            </div>

                                            <div className="d-flex align-items-center mb-2">
                                                <step.icon size={22} className="me-2" style={{ color: step.color }} />
                                                <h3 className="h5 fw-bold text-white m-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                    {step.title}
                                                </h3>
                                            </div>

                                            <h4 className="fw-bold mb-3" style={{ color: step.color, fontSize: '0.95rem' }}>{step.heading}</h4>

                                            <p className="mb-3 flex-grow-1" style={{ color: '#f8fafc', fontSize: '0.95rem', lineHeight: '1.65' }}>
                                                {step.description}
                                            </p>

                                            {step.command && (
                                                <div className="p-3 mb-2 bg-dark rounded border border-warning d-flex align-items-center gap-2">
                                                    <FaTerminal size={16} className="text-neon-cyan" />
                                                    <span className="font-monospace text-neon-gold fs-6 fw-bold">{step.command}</span>
                                                </div>
                                            )}

                                            {step.action}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="isolation-tab"
                            initial={{ opacity: 0, y: 15 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -15 }}
                            transition={{ duration: 0.3 }}
                        >
                            {/* Section Header */}
                            <div className="d-flex align-items-center mb-4">
                                <div className="p-3 me-3 rounded-3" style={{ background: 'rgba(255, 0, 60, 0.15)', border: '1px solid #ff003c', color: '#ff003c' }}>
                                    <FaLock size={28} />
                                </div>
                                <div>
                                    <h2 className="h4 fw-bold text-white mb-1" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                        DISCIPLINE, REPORTING & ISOLATION SYSTEM
                                    </h2>
                                    <p className="mb-0 fw-semibold" style={{ color: '#cbd5e1', fontSize: '1rem' }}>
                                        Our structured framework for player reporting, isolation rehabilitation, and zero-tolerance policies
                                    </p>
                                </div>
                            </div>

                            <Row className="g-4">
                                {/* Reporting & Incident Management */}
                                <Col lg={6}>
                                    <div
                                        className="p-4 h-100 rounded-4 position-relative overflow-hidden d-flex flex-column"
                                        style={{
                                            background: 'rgba(22, 26, 34, 0.85)',
                                            backdropFilter: 'blur(12px)',
                                            border: '1px solid rgba(255, 215, 0, 0.35)',
                                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.5)'
                                        }}
                                    >
                                        <div className="d-flex align-items-center mb-3">
                                            <FaExclamationTriangle size={26} className="text-neon-gold me-3" />
                                            <h3 className="h5 fw-bold text-white m-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                IN-GAME REPORTING & COMPLAINTS
                                            </h3>
                                        </div>

                                        <p className="fw-semibold mb-3" style={{ color: '#ffffff', fontSize: '1rem' }}>
                                            To streamline administration, external support ticket submissions are disabled. All player reports are handled via direct in-game oversight:
                                        </p>

                                        <ul className="ps-3 mb-0 flex-grow-1" style={{ color: '#f8fafc', fontSize: '0.95rem', lineHeight: '1.85' }}>
                                            <li className="mb-2"><strong className="text-neon-gold">Direct Player Reporting:</strong> Submit reports containing character names or identifying details directly within the city.</li>
                                            <li className="mb-2"><strong className="text-neon-gold">Active Staff Oversight:</strong> Reported individuals are placed under staff surveillance. Repeat or continued misconduct triggers immediate administrative intervention.</li>
                                            <li><strong className="text-neon-gold">Continuous Monitoring:</strong> Staff review intent, context, and history before taking action.</li>
                                        </ul>
                                    </div>
                                </Col>

                                {/* Task-Based Isolation Instance */}
                                <Col lg={6}>
                                    <div
                                        className="p-4 h-100 rounded-4 position-relative overflow-hidden d-flex flex-column"
                                        style={{
                                            background: 'rgba(22, 26, 34, 0.85)',
                                            backdropFilter: 'blur(12px)',
                                            border: '1px solid rgba(0, 243, 255, 0.35)',
                                            boxShadow: '0 8px 30px rgba(0, 0, 0, 0.5)'
                                        }}
                                    >
                                        <div className="d-flex align-items-center mb-3">
                                            <FaUserSlash size={26} className="text-neon-cyan me-3" />
                                            <h3 className="h5 fw-bold text-white m-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                ISOLATION INSTANCE (NO TRADITIONAL BANS)
                                            </h3>
                                        </div>

                                        <p className="fw-semibold mb-3" style={{ color: '#ffffff', fontSize: '1rem' }}>
                                            Rather than issuing standard bans for Fail RP or general rule breaks, offending players are placed into an isolated server instance:
                                        </p>

                                        <ul className="ps-3 mb-0 flex-grow-1" style={{ color: '#f8fafc', fontSize: '0.95rem', lineHeight: '1.85' }}>
                                            <li className="mb-2"><strong className="text-neon-cyan">Isolated Dimension:</strong> Isolated players cannot see or interact with active RP players in the main city. Only isolated players coexist in this realm.</li>
                                            <li className="mb-2"><strong className="text-neon-cyan">Corrective Task Sets:</strong> Based on the severity of the rule violation, mandatory rehabilitation tasks are assigned.</li>
                                            <li><strong className="text-neon-cyan">Officer Validation:</strong> Upon completing all assigned tasks, the player must report to the Officer for validation. Once approved, the player is restored to the regular RP instance.</li>
                                        </ul>
                                    </div>
                                </Col>

                                {/* Zero Tolerance Notice */}
                                <Col lg={12}>
                                    <div
                                        className="p-4 rounded-4 position-relative overflow-hidden border border-danger"
                                        style={{
                                            background: 'linear-gradient(135deg, rgba(255, 0, 60, 0.25), rgba(18, 18, 22, 0.95))',
                                            boxShadow: '0 0 35px rgba(255, 0, 60, 0.3)'
                                        }}
                                    >
                                        <Row className="align-items-center">
                                            <Col lg={8}>
                                                <div className="d-flex align-items-center mb-2">
                                                    <FaGavel size={28} className="text-danger me-3" />
                                                    <h3 className="h5 fw-bold text-white m-0" style={{ fontFamily: 'Orbitron, sans-serif' }}>
                                                        SEVERE VIOLATIONS & IMMEDIATE PERMANENT BANS
                                                    </h3>
                                                </div>
                                                <p className="mb-0 fw-semibold" style={{ color: '#ffffff', fontSize: '1rem', lineHeight: '1.75' }}>
                                                    <strong className="text-danger">CRITICAL NOTICE:</strong> The Isolation System applies strictly to standard Fail RP and operational infractions. <br />
                                                    <strong>Abusive behavior, disrespecting female characters, toxicity, or harassment towards fellow players</strong> will result in an <strong>IMMEDIATE PERMANENT BAN</strong> without prior warning, notice, or public announcement.
                                                </p>
                                            </Col>
                                            <Col lg={4} className="mt-3 mt-lg-0 text-lg-end">
                                                <div className="p-3 bg-dark border border-danger rounded text-center">
                                                    <Badge bg="danger" className="mb-2 px-3 py-1 fs-6 tracking-widest text-uppercase">STRICT POLICY</Badge>
                                                    <p className="mb-0 fw-bold text-white" style={{ fontSize: '0.9rem' }}>No Appeals or OOC Discussions will be entertained for severe infractions.</p>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                </Col>
                            </Row>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Container>
        </div>
    );
};

export default Info;
